import { Alert, Box, Button, Dialog, DialogContent } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  bulkLabelLotNdcReviewApi,
  commitBulkLabelLotNdcApi,
  regenerateLabelsApi,
} from 'api/requests';
import { AddManualLotEnabled, BlockUnusableLotItems } from 'common/utils/featureFlags';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';

import BulkLotNdcCommitAction from './BulkLotNdcCommitAction';
import BulkLotNdcCommitProgress, { COMMIT_STAGE } from './BulkLotNdcCommitProgress';
import BulkLotNdcExcludedPanel from './BulkLotNdcExcludedPanel';
import BulkLotNdcRefusedPanel from './BulkLotNdcRefusedPanel';
import BulkLotNdcReviewHeader from './BulkLotNdcReviewHeader';
import BulkLotNdcPrintBar from './BulkLotNdcPrintBar';
import ChangeNdcLotDialog from './ChangeNdcLotDialog';
import DiscardLotNdcChangesDialog from './DiscardLotNdcChangesDialog';
import BulkLotNdcReviewTable from './BulkLotNdcReviewTable';
import BulkLotNdcReviewFilters, { ALL } from './BulkLotNdcReviewFilters';
import {
  buildCommitChanges,
  isSameAsRecorded,
  stageLotNdcChange,
  stagedUnverifiedQty,
} from '../utils/bulkLotNdcStaging';
import { batchLotContextForRow } from '../utils/bulkManualLotEntry';
import { ROW_STATUS, classifyRows, excludedLineItemIds } from '../utils/bulkLotNdcStatus';
import {
  autoIncludedSummary,
  excludedReasonSummary,
  listedRowsSummary,
  printableSummary,
} from '../utils/bulkLotNdcSummaries';
import BulkLotNdcReviewCaptions from './BulkLotNdcReviewCaptions';
import BulkLotNdcReviewEmptyState, { REVIEW_EMPTY } from './BulkLotNdcReviewEmptyState';
import BulkLotNdcReviewLoadState from './BulkLotNdcReviewLoadState';
import useCommittedLabelRegeneration from '../hooks/useCommittedLabelRegeneration';
import bulkLotNdcHelperText, { LABEL_BUILD } from '../utils/bulkLotNdcHelperText';
import { useReviewSessionId } from '../utils/reviewSession';

/**
 * POM-6107 — `showRecommended` rides with the filters rather than being its own state so it resets
 * on every open for free, through the `setFilters(NO_FILTERS)` the open effect already runs. It is
 * off by default: a Recommended row has been checked and needs no decision, so the grid opens on
 * the rows that do.
 */
const NO_FILTERS = { drug: ALL, facility: ALL, partner: ALL, status: ALL, showRecommended: false };

const todayString = () => new Date().toISOString().slice(0, 10);

/**
 * POM-6010 — the Review Lot & NDC step between "Print Labels" and label generation.
 *
 * One screen, not two: the grid stays on view and the real Download / Print / Mark as Printed
 * controls live in this dialog's bottom bar, supplied by the caller through `renderPrintActions`
 * and locked while there is anything uncommitted. That is why the caller passes a render function
 * rather than a node — the buttons' own enablement rules (is the PDF built, may this user mark
 * printed) belong to the caller, and the "is the batch settled" half belongs here.
 *
 * It owns the uncommitted-change state and nothing else: every status, count and blocking decision
 * comes from `classifyRows`, so this component renders what it is told rather than re-deriving
 * inventory rules.
 */
/**
 * POM-6121 — both switches, not either.
 *
 * BlockUnusableLotItems says a label needs a lot that can be dispensed; AddManualLotEnabled is
 * what gives the operator a way to supply one. With manual entry off the premise does not arise,
 * so the whole rule is off and POM-6010's behaviour — including the opt-out — stands exactly as
 * it was. Module scope, because both halves are build-time constants: nothing in a render can
 * change it, and deriving it here is what stops a reader using one without the other.
 */
const blockUnusableLotItems = AddManualLotEnabled && BlockUnusableLotItems;

const BulkLotNdcReviewDialog = ({
  open,
  requestLineItemIds,
  onSettled,
  onClose,
  printNotices,
  renderPrintNotice,
  renderPrintActions,
  printProgress,
  labelBuild,
}) => {
  // Settled means "a PDF has been requested for the current state of the batch". It gates the
  // print controls and silences the discard prompt for changes that are already committed.
  const [settled, setSettled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [changes, setChanges] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  // Which half of the commit is running, or null. `committing` stays a boolean on top of it so
  // every gate that already depended on it (the Commit spinner, the print lock, Close) is
  // unchanged — the stage only drives what the user is told.
  const [commitStage, setCommitStage] = useState(null);
  const [regeneratedCount, setRegeneratedCount] = useState(0);
  const [commitResults, setCommitResults] = useState(null);
  const [regenerateFailedIds, setRegenerateFailedIds] = useState([]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // How many problem rows the user has asked to print ANYWAY. Dropping them is the default, so
  // this records the opt-out from it, and it is held as a count rather than a boolean so the box
  // re-ticks itself when the number changes — an agreement to print 1 label whose lot may be wrong
  // must not silently become an agreement to print 3.
  const [printAnywayOptIn, setPrintAnywayOptIn] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [search, setSearch] = useState('');

  /**
   * POM-6121 — cross-NDC lookups already answered in this sitting.
   *
   * One vial commonly covers several rows of a batch, so the same number is typed again and again.
   * Held here rather than in the picker because the picker is mounted per row, and cleared with
   * everything else when the popup opens — the answer only has to be good for one sitting, and the
   * commit re-validates server-side regardless.
   *
   * Cleared in place with `.clear()`, never reassigned: the hook captures this Map once and keeps
   * reading the captured reference, so a fresh `new Map()` here would leave it serving the
   * previous sitting's answers for the rest of the mount.
   */
  const lotLookupCache = useRef(new Map());

  // One id for this whole popup open: the review read, every commit, and the post-commit re-read
  // all report under it, so the audit trail groups them as one sitting.
  const reviewSessionId = useReviewSessionId(open);

  // The commit queues the label rebuild itself, so this waits for a run it did not start.
  const labels = useCommittedLabelRegeneration();

  const committing = commitStage !== null;

  /* Two different things rebuild labels from this dialog, and they must not draw two bars.
   *
   * A commit queues its own rebuild and reports through `labels`. The Regenerate Label button in
   * the print bar starts a run this dialog does not own — the caller holds that state and passes
   * it down as `printProgress`. A commit wins when both are somehow live: it is the one holding
   * the dialog locked, so it is the one the user is waiting on.
   *
   * The bar goes indeterminate when the socket is down. `printProgress.total` is the submitted
   * count and is known up front, but without realtime no label ever reports back, so a
   * determinate bar would sit at 0% for the whole run and read as a hang. Handing `total: 0`
   * down reuses the truthfulness rule BulkLotNdcCommitProgress already applies to commits. */
  const manualRegenerating = !committing && !!printProgress?.isTracking;
  const progressStage =
    commitStage ?? (manualRegenerating ? COMMIT_STAGE.REGENERATING_LABELS : null);
  const progressCompleted = manualRegenerating
    ? (printProgress.completedCount ?? 0)
    : labels.completedCount;
  const manualTotal = printProgress?.isRealtimeAvailable ? (printProgress.total ?? 0) : 0;
  const progressTotal = manualRegenerating ? manualTotal : labels.total;

  const todayStr = todayString();

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setLoadError(null);
    setChanges({});
    setCommitResults(null);
    setRegenerateFailedIds([]);
    setFilters(NO_FILTERS);
    setSearch('');
    setSettled(false);
    setPrintAnywayOptIn(null);
    setCommitStage(null);
    setRegeneratedCount(0);
    lotLookupCache.current.clear();

    bulkLabelLotNdcReviewApi({ requestLineItemIds, reviewSessionId })
      .then(setPayload)
      /* NOT error.message: graphql-request's ClientError puts the whole response and request in
         it, so that rendered a wall of JSON -- headers, CloudFront ids, the entire query text --
         inside the dialog. getGraphQLErrorMessage takes the one useful sentence, and genericises
         framework codes whose raw text is written for developers. */
      .catch((error) => setLoadError(getGraphQLErrorMessage(error)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { rows, counts } = useMemo(
    () =>
      classifyRows({
        items: payload?.items ?? [],
        availability: payload?.availability ?? [],
        // POM-6121: whether a row may be hand-recorded is part of whether it can be edited at
        // all, so the classifier needs this alongside the availability snapshot.
        recordableLots: payload?.recordableLots ?? [],
        todayStr,
        changes,
        // The selection, not the payload: the review drops line items it cannot find or that fall
        // outside the caller's fulfilling pharmacies, and every count on this screen is compared
        // against what the user picked.
        selectedCount: requestLineItemIds.length,
        // Passed in rather than read inside the classifier, which is a pure module. The picker
        // gates on the same flag, so a row it calls editable must be one the dialog can act on.
        isManualLotEnabled: AddManualLotEnabled,
        // POM-6121 — its own switch, so refusing a lot-less fill can be stopped without taking
        // hand-recording down with it.
        blockUnusableLotItems,
      }),
    [payload, changes, todayStr, requestLineItemIds]
  );

  // settle() runs at the end of async handlers, and by then the classification it must read has
  // usually moved on — a commit refreshes the payload mid-flight. A ref keeps it reading the
  // latest rows rather than the ones captured when the handler was created.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Read through a ref for the same reason as rowsRef: settle() runs at the end of async handlers,
  // by which time a captured value can be a toggle behind.
  const excludeRef = useRef(false);

  const settle = useCallback(() => {
    // POM-6121 — the rows that cannot carry a correct lot are left out once the operator has
    // acknowledged it. Leaving them out is what stops them printing AND what stops them being
    // filled: an id that is not in this list never reaches markLineItemsPrinted, so the fill
    // that follows it cannot include it either.
    const blockedIds = excludedLineItemIds({
      rows: rowsRef.current,
      excludeBlocked: excludeRef.current,
    });

    setSettled(true);
    onSettled({
      printableLineItemIds: requestLineItemIds.filter((id) => !blockedIds.has(id)),
    });
  }, [requestLineItemIds, onSettled]);

  /**
   * Build the PDF as soon as the review data lands, so Download and Print are live while the user
   * reads the grid. A batch that then gets edited pays for a second build after its commit — the
   * cost of the labels being ready for the majority of batches that need no change at all.
   */
  const unverifiedTotal = stagedUnverifiedQty(changes);

  const readyToBuild = !!payload && !loading && !settled && counts.changed === 0 && !committing;

  useEffect(() => {
    if (readyToBuild) settle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToBuild]);

  const availabilityFor = (item) =>
    (payload?.availability ?? []).find(
      (entry) =>
        entry.productId === item.productId &&
        entry.fulfillingPharmacyId === item.fulfillingPharmacyId
    );

  /** POM-6121 — what a lot may be hand-recorded against, keyed the same way availability is. */
  const recordableFor = (item) =>
    (payload?.recordableLots ?? []).find(
      (entry) =>
        entry.productId === item.productId &&
        entry.fulfillingPharmacyId === item.fulfillingPharmacyId
    );

  const visibleRows = rows.filter(({ item, status }) => {
    // A display filter, not a classification: `counts`, the banner and the printed batch all still
    // see these rows. An edited one is safe from it too — the classifier reaches CHANGED before
    // AS_ALLOCATED, so a row holding an uncommitted change cannot be hidden out from under it.
    if (!filters.showRecommended && status === ROW_STATUS.AS_ALLOCATED) return false;
    if (filters.drug !== ALL && item.medicationName !== filters.drug) return false;
    if (filters.facility !== ALL && item.fulfillingPharmacyName !== filters.facility) return false;
    if (filters.partner !== ALL && item.partner !== filters.partner) return false;
    if (filters.status !== ALL && status !== filters.status) return false;

    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      const haystack = `${item.orderId ?? ''} ${item.rxNumber ?? ''}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }

    return true;
  });

  /**
   * Stage one Apply, through stageLotNdcChange rather than straight into the map: a selection
   * equal to what the row already records is not a change, and staging it badged the row Changed,
   * held the print bar until a commit, and sent core a write it reports as already applied. The
   * picker disables Apply there; this is the rule itself, so the map cannot hold a no-op however
   * Apply was reached.
   */
  const handleApply = ({ requestLineItemId, ndc, lotAllocations }) => {
    const { item } = editingRow ?? {};

    setChanges((current) =>
      stageLotNdcChange({ changes: current, item, requestLineItemId, ndc, lotAllocations })
    );
    setEditingRow(null);

    // Only a real edit invalidates the labels already built for the batch. Putting a row back to
    // what it records leaves them correct, so the earlier confirmation still stands.
    if (!isSameAsRecorded({ item, ndc, lotAllocations })) setRegeneratedCount(0);
  };

  const handleUndo = useCallback(
    (requestLineItemId) =>
      setChanges((current) => {
        const next = { ...current };
        delete next[requestLineItemId];

        return next;
      }),
    []
  );

  /**
   * Re-read the review after a commit.
   *
   * Required, not a nicety: committed rows are dropped from local `changes`, so without a refresh
   * every fixed row falls back to its pre-commit classification and keeps reading as expired — it
   * would stay counted as excluded, and the acknowledgement below would be gating on a number
   * that is already wrong.
   */
  const refreshPayload = async () => {
    try {
      setPayload(await bulkLabelLotNdcReviewApi({ requestLineItemIds, reviewSessionId }));
    } catch {
      // The commit itself already succeeded; a stale grid is not worth failing the flow over.
    }
  };

  /**
   * Wait for the labels the commit queued.
   *
   * Deliberately does NOT settle: it runs alongside refreshPayload, and settle() reads the
   * classification that refresh replaces. Its callers settle once they have both halves.
   *
   * We no longer ask for the rebuild — commitBulkLabelLotNdc queues it for the rows that
   * committed, and asking again would put two runs over the same line items in flight. This only
   * waits, over the socket or the status poll, whichever answers first.
   *
   * Split out of handleCommit so the retry can re-run JUST this: by the time the warning is on
   * screen the commit has already succeeded and `changes` has been cleared, so re-entering
   * handleCommit would find nothing to send and silently do nothing.
   */
  const awaitRegeneration = async (idsToRegenerate) => {
    setRegenerateFailedIds([]);

    const outcome = await labels.waitForCommittedLabels(idsToRegenerate);

    if (outcome.pending.length > 0) {
      // Accepted risk: still printable, but say loudly which labels may still show the previous
      // lot. "May" is the honest word on a timeout — the run might yet land after we gave up.
      setRegenerateFailedIds(outcome.pending);
      setRegeneratedCount(0);
    } else {
      setRegeneratedCount(outcome.completed);
    }
  };

  /**
   * The one place the portal still asks for a regeneration itself.
   *
   * Only reachable once a run has already failed to dispatch or could not be confirmed, and only
   * on an explicit click — so there is nothing in flight for it to race.
   */
  const handleRetryRegenerate = async () => {
    const ids = [...regenerateFailedIds];
    if (ids.length === 0) return;

    setCommitStage(COMMIT_STAGE.REGENERATING);
    try {
      await regenerateLabelsApi(ids);
      await awaitRegeneration(ids);
      // Settles inline, unlike the commit paths: no refresh is in flight here, so the rows this
      // reads are already the current ones.
      settle();
    } catch {
      setRegenerateFailedIds(ids);
      setRegeneratedCount(0);
      settle();
    } finally {
      setCommitStage(null);
    }
  };

  const handleCommit = async () => {
    // Shaped by bulkLotNdcStaging, which owns what a staged change looks like both going in and
    // going out — including the hand-recorded fields POM-6121 added.
    const payloadChanges = buildCommitChanges(changes);

    if (payloadChanges.length === 0) return;

    setCommitStage(COMMIT_STAGE.COMMITTING);
    setRegenerateFailedIds([]);
    setRegeneratedCount(0);

    try {
      const result = await commitBulkLabelLotNdcApi({
        changes: payloadChanges,
        reviewSessionId,
      });
      setCommitResults(result.results ?? []);

      const committedIds = (result.results ?? [])
        .filter((row) => row.success)
        .map((row) => row.requestLineItemId);

      // Refused rows revert to their original lot, so drop them from local state; the results
      // panel is what tells the user they were refused.
      setChanges((current) => {
        const next = { ...current };
        (result.results ?? []).forEach((row) => delete next[row.requestLineItemId]);

        return next;
      });

      if (committedIds.length === 0) {
        return;
      }

      setCommitStage(COMMIT_STAGE.REGENERATING);

      // The writes landed but core could not get the rebuild onto the queue. Nothing is coming,
      // so do not wait for it — name the labels and offer the retry straight away.
      if (!result.labelRegeneration?.queued) {
        await refreshPayload();
        setRegenerateFailedIds(committedIds);
        setRegeneratedCount(0);
        // Same reason as the queued path below.
        setSettled(false);

        return;
      }

      /* Listening starts BEFORE the refetch, not after it. Core queued the rebuild inside the
       * mutation we just awaited, so labels can begin landing immediately — and refreshPayload is
       * the slowest call in the flow, since bulkLabelLotNdcReview fans out over inventory per
       * product and facility. Every per-label event that arrived during it used to be dropped,
       * which left the bar indeterminate and the socket unable to settle on its own count. */
      await Promise.all([awaitRegeneration(committedIds), refreshPayload()]);

      // Hand the settle to readyToBuild rather than calling it here. settle() reads the
      // classification, and awaiting refreshPayload only SCHEDULES the state update that
      // re-classifies — inline is always a render too early. A row that blocked before the commit
      // then settled on an empty printable set and the print bar latched shut over it.
      setSettled(false);
    } catch (error) {
      setLoadError(getGraphQLErrorMessage(error));
    } finally {
      setCommitStage(null);
    }
  };

  const handleCloseRequest = () => {
    if (counts.changed > 0) {
      setConfirmDiscard(true);

      return;
    }
    onClose();
  };

  /**
   * Escape closes (through the same guard the buttons use); a backdrop click does not.
   *
   * A stray click beside a full-width review is far too easy, and it would throw away every
   * uncommitted decision the user has made so far.
   */
  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick') return;
    handleCloseRequest();
  };

  // Problem rows do not hold the bar. They are dropped from the batch by default and the rest
  // prints, so there is nothing to acknowledge — the panel below states what will happen either
  // way, and unticking it puts them back. Uncommitted edits still hold the bar: printing then
  // would put the superseded lot on the label.
  /**
   * POM-6121 — with the switch on there is no way back in.
   *
   * The opt-out existed because a label carrying a stale lot was still thought worth having. It
   * is not: a lot that cannot be dispensed makes the label wrong, and no lot at all makes it
   * empty. Neither is a label anyone can work from, so the only ways forward are to fix the row
   * in the grid or to let it go and deal with it in the queue.
   *
   * Off, the opt-out is exactly as it was.
   */
  const excludeBlocked =
    counts.blocked > 0 && (blockUnusableLotItems || printAnywayOptIn !== counts.blocked);
  excludeRef.current = excludeBlocked;
  const printLocked = committing || counts.changed > 0 || !settled;
  // The same rule minus the acknowledgement, for the bar's Regenerate control: rebuilding the
  // labels is not printing them, so it has no business waiting on the user to agree to drop a row.
  // It still waits on anything in flight, and on uncommitted edits — committing those rebuilds the
  // labels itself, so regenerating first would only rebuild the ones about to be replaced.
  const actionsBusy = committing || counts.changed > 0 || !settled;

  const batchLotContext = useMemo(
    () => batchLotContextForRow({ items: payload?.items, changes, row: editingRow?.item }),
    [payload?.items, changes, editingRow]
  );

  const helperText = bulkLotNdcHelperText({
    commitStage,
    counts,
    settled,
    excludeBlocked,
    labelBuild,
    blockUnusableLotItems,
  });

  /**
   * Changing your mind about the problem rows changes WHICH labels the PDF contains, so the built
   * one is stale. Clearing `settled` is what rebuilds it: readyToBuild picks up again and settle()
   * re-reports the new id set.
   *
   * Watching the user's own choice rather than the derived `excludeBlocked`, because that flag
   * necessarily goes false → true the moment the payload lands and the blocked count stops being 0.
   * Exclusion is the DEFAULT, so treating THAT as a change of mind would clear `settled` in the
   * same flush the first build set it, and readyToBuild would not fire again — its own condition
   * never changed — leaving the print bar locked for good.
   */
  useEffect(() => {
    setSettled(false);
  }, [printAnywayOptIn]);

  const excludedPanel = counts.blocked > 0 && (
    <BulkLotNdcExcludedPanel
      blockedCount={counts.blocked}
      printableCount={counts.printable}
      reason={excludedReasonSummary(rows)}
      excluded={excludeBlocked}
      canPrintAnyway={!blockUnusableLotItems}
      /* POM-6119: can the operator do anything about these HERE? A row with no inventory master,
         no NDC options, or one core refuses a hand-recorded lot for, blocks with no pencil — and
         telling them to fix it in the grid is then a dead end. */
      anyFixableHere={rows.some((row) => row.blocksBatch && row.canEdit)}
      onToggle={() => setPrintAnywayOptIn(excludeBlocked ? counts.blocked : null)}
    />
  );

  const refused = (commitResults ?? []).filter((row) => !row.success);

  return (
    <>
      {/*
        Fullscreen, not a capped panel.

        The grid carries nine columns plus a right-hand tool panel, and at anything narrower than
        the viewport both the NDC/Lot columns and the horizontal scrollbar were competing for the
        same space. Nothing else can be on screen while this is open anyway — every control the
        user needs is inside it — so the dialog frame was costing width and buying nothing.

        fullScreen also gives the grid the definite height box it needs: it fills its container,
        and without one the dialog grows with the row count and pushes its own bottom bar — the
        Print controls — off the screen.
      */}
      <Dialog open={open} onClose={handleDialogClose} fullScreen>
        <BulkLotNdcReviewHeader
          selectedCount={requestLineItemIds.length}
          committing={committing}
          onClose={handleCloseRequest}
        />

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <BulkLotNdcCommitProgress
            stage={progressStage}
            regeneratedCount={regeneratedCount}
            completed={progressCompleted}
            total={progressTotal}
          />

          <BulkLotNdcReviewLoadState
            loading={loading}
            itemCount={requestLineItemIds.length}
            loadError={loadError}
          />

          {!loading && payload && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Alert severity='info' sx={{ mb: 2, flexShrink: 0 }}>
                {`${printableSummary({
                  total: counts.total,
                  blocked: counts.blocked,
                  excluded: excludeBlocked,
                })} `}
                {autoIncludedSummary(counts)}
                {listedRowsSummary({ ...counts, excluded: excludeBlocked })}
                {/*
                  Said out loud rather than absorbed into the total. These items print exactly as
                  they did before this screen existed, but nothing here has checked their lot, and
                  a count that quietly excluded them was the screen disagreeing with what printed.
                */}
                {counts.unreviewed > 0 &&
                  ` ${counts.unreviewed} could not be reviewed (no longer in the queue, or outside your facilities) and print unchanged.`}
              </Alert>

              {counts.listed > 0 && (
                // minHeight: 0 on every flex ancestor is what lets the grid actually resize —
                // a flex item defaults to min-height:auto and would otherwise refuse to shrink
                // below its content, pushing the bottom bar off screen instead.
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <BulkLotNdcReviewFilters
                    rows={rows}
                    filters={filters}
                    onChange={setFilters}
                    search={search}
                    onSearchChange={setSearch}
                    trailingAction={
                      <BulkLotNdcCommitAction
                        changedCount={counts.changed}
                        committing={committing}
                        onCommit={handleCommit}
                      />
                    }
                  />

                  <BulkLotNdcReviewCaptions
                    shownCount={visibleRows.length}
                    listedCount={counts.listed}
                    autoIncludedCount={counts.autoIncluded}
                    unverifiedQty={unverifiedTotal}
                  />

                  <BulkLotNdcReviewTable
                    visibleRows={visibleRows}
                    changes={changes}
                    onEdit={setEditingRow}
                    onUndo={handleUndo}
                  />
                </Box>
              )}

              {/* Told apart by whether anything was actually checked, not by the row count alone:
                  a batch nobody could review has no rows either, and calling that all-clear would
                  vouch for lots this screen never looked at. */}
              {counts.listed === 0 && (
                <BulkLotNdcReviewEmptyState
                  variant={
                    counts.autoIncluded > 0 ? REVIEW_EMPTY.ALL_CLEAR : REVIEW_EMPTY.NOT_REVIEWED
                  }
                />
              )}
            </Box>
          )}

          {excludedPanel}

          {/* The notices belong to the print controls, so they stay with them at the bottom. */}
          {settled && printNotices}

          <BulkLotNdcRefusedPanel refused={refused} />

          {regenerateFailedIds.length > 0 && (
            <Alert severity='warning' sx={{ mt: 2 }}>
              {`${regenerateFailedIds.length} label(s) could not be rebuilt and still show the previous lot. The change was saved — retry the regeneration before printing these.`}
              <Box>
                <Button size='small' onClick={handleRetryRegenerate} disabled={committing}>
                  Retry
                </Button>
              </Box>
            </Alert>
          )}
        </DialogContent>

        <BulkLotNdcPrintBar
          counts={counts}
          excludedCount={excludeBlocked ? counts.blocked : 0}
          helperText={helperText}
          printLocked={printLocked}
          actionsBusy={actionsBusy}
          committing={committing}
          renderPrintNotice={renderPrintNotice}
          renderPrintActions={renderPrintActions}
          onClose={handleCloseRequest}
        />
      </Dialog>

      {editingRow && (
        <ChangeNdcLotDialog
          open
          item={editingRow.item}
          availability={availabilityFor(editingRow.item)}
          recordable={recordableFor(editingRow.item)}
          sessionManualLots={batchLotContext.sessionManualLots}
          batchNdcClaims={batchLotContext.ndcClaims}
          lotLookupCache={lotLookupCache.current}
          change={changes[editingRow.item.requestLineItemId] ?? null}
          todayStr={todayStr}
          onApply={handleApply}
          onCancel={() => setEditingRow(null)}
        />
      )}

      <DiscardLotNdcChangesDialog
        open={confirmDiscard}
        changedCount={counts.changed}
        onKeepEditing={() => setConfirmDiscard(false)}
        onDiscard={() => {
          setConfirmDiscard(false);
          setChanges({});
          onClose();
        }}
      />
    </>
  );
};

BulkLotNdcReviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  requestLineItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSettled: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  printNotices: PropTypes.node,
  renderPrintNotice: PropTypes.func,
  renderPrintActions: PropTypes.func,
  // The caller's live state for a Regenerate Label run it started from this dialog's print bar.
  printProgress: PropTypes.shape({
    isTracking: PropTypes.bool,
    completedCount: PropTypes.number,
    total: PropTypes.number,
    isRealtimeAvailable: PropTypes.bool,
  }),
  /**
   * Where the caller's PDF build has got to. A different question from printProgress above: that
   * one counts labels being rebuilt, this one is whether the merged PDF those labels go into
   * exists yet. Owned by the caller, not here — settle() hands it an id set and it fetches the
   * file, so this dialog is told the outcome rather than deriving it. Only the helper line reads
   * it; the buttons already gate on the URL itself.
   */
  labelBuild: PropTypes.oneOf(Object.values(LABEL_BUILD)),
};

BulkLotNdcReviewDialog.defaultProps = {
  printNotices: null,
  renderPrintNotice: () => null,
  renderPrintActions: () => null,
  printProgress: null,
  labelBuild: LABEL_BUILD.READY,
};

export default BulkLotNdcReviewDialog;
